// Pure helpers for editing a voucher batch (PUT /voucherBatch/{clinic}/{id}).
// The backend (voucherRepo.updateBatch) is the authority on what each status
// may change; these only shape the request so the form never sends a field the
// batch's status will refuse. Pinned by tests/unit/voucherBatchEdit.spec.ts.

/** CLOSED is final — no edits. */
export const EDITABLE_STATUSES = ["DRAFT", "ACTIVE"];

const toDate = (v) => (v ? String(v).slice(0, 10) : null);
const toNum = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
const orNull = (arr) => (arr && arr.length ? arr : null);
const trimOrNull = (s) => (s && s.trim() ? s.trim() : null);

/** A batch from getBatchByID (with its hidden discount) → VoucherBatchForm's form shape. */
export function batchToForm(batch) {
  const d = batch.discount || {};
  return {
    name: batch.name || "",
    codePrefix: batch.codePrefix || "",
    quantity: batch.quantity,
    scope: d.scope || "RECEIPT",
    channelNote: batch.channelNote || "",
    validFrom: toDate(batch.validFrom),
    validTo: toDate(batch.validTo),
    discountType: Number(d.discountType),
    discountValue: toNum(d.discountValue) ?? 0,
    discountMethod: Number(d.discountMethod) || 0,
    doctorDiscountValue: toNum(d.doctorDiscountValue),
    clinicDiscountValue: toNum(d.clinicDiscountValue),
    maxDiscountPercent: toNum(d.maxDiscountPercent),
    // 0 is a real minimum, so null/undefined — not falsy — means "none".
    minReceiptAmount: toNum(d.minReceiptAmount),
    branchIds: d.branchIds || [],
    doctorIds: d.doctorIds || [],
    patientGroupIds: d.patientGroupIds || [],
    treatments: (d.treatments || []).map((t) => ({
      treatId: t.treatId, treatCateId: t.treatCateId, itemId: t.itemId,
    })),
  };
}

/**
 * The PUT body for an edit. DRAFT sends every editable field (null clears);
 * ACTIVE sends only name, note, and validTo when it actually moved — its cards
 * are in circulation, so the value and rules are not the form's to change.
 *
 * @param {object} form VoucherBatchForm's form
 * @param {{status: string, validTo: string|null}} original the batch as loaded
 */
export function buildBatchUpdatePayload(form, original) {
  const data = { name: form.name.trim(), channelNote: trimOrNull(form.channelNote) };

  if (original.status === "ACTIVE") {
    if ((form.validTo || null) !== toDate(original.validTo)) {
      data.validTo = form.validTo || null;
    }
    return data;
  }

  const isSpecific = Number(form.discountMethod) === 1;
  return {
    ...data,
    validFrom: form.validFrom || null,
    validTo: form.validTo || null,
    discountType: Number(form.discountType),
    discountValue: Number(form.discountValue),
    discountMethod: Number(form.discountMethod),
    doctorDiscountValue: isSpecific ? Number(form.doctorDiscountValue || 0) : null,
    clinicDiscountValue: isSpecific ? Number(form.clinicDiscountValue || 0) : null,
    // Both scopes carry a ceiling: ITEM against the line's price, RECEIPT
    // against the eligible lines' total (same rule as the create payload).
    maxDiscountPercent: toNum(form.maxDiscountPercent),
    minReceiptAmount: toNum(form.minReceiptAmount),
    branchIds: orNull(form.branchIds),
    doctorIds: orNull(form.doctorIds),
    patientGroupIds: orNull(form.patientGroupIds),
    treatments: form.treatments || [],
  };
}

/** OPEN = not yet closed (DRAFT + ACTIVE); ALL = everything; otherwise an exact status. */
export function filterBatchesByStatus(batches, filter) {
  if (filter === "ALL") return batches;
  if (filter === "OPEN") return batches.filter((b) => EDITABLE_STATUSES.includes(b.status));
  return batches.filter((b) => b.status === filter);
}
